import React, { Fragment, useCallback, useState } from 'react';
import { Button, FormModifier, Headline, IconLogoMark, Input, Link, Subhead, Text } from '@hudl/uniform-ui-components';
import g11n from 'hudl-g11n';
import * as styles from './styles.scss';
import ErrorDisplay from 'components/shared/ErrorDisplay';
import { CreatePasswordResponse } from 'requests/rest/createPassword';
import PageBackground from 'components/shared/PageBackground';
import { useTitle } from 'hooks/useTitle';
const { FormattedHTMLMessage } = g11n.ReactIntl;

interface CreatePasswordPageProps {
  email: string | null;
  isValidToken: boolean;
  onCreatePassword: (newPassword: string, confirmNewPassword: string) => Promise<CreatePasswordResponse>;
}

const getErrorMessage = (error: number | null): string => {
  switch (error) {
    case 1: // PasswordStatus.InternalError:
      return g11n.format('login_reset_problem');
    case 2: // PasswordStatus.BlankPassword:
      return g11n.format('login_error_blank_password');
    case 3: // PasswordStatus.PasswordsNoMatch:
      return g11n.format('login_error_mismatched_password');
    case 4: // PasswordStatus.WeakPassword:
      return g11n.format('login_error_weak_password');
    default:
      return g11n.format('login_reset_problem');
    // case 5: PasswordStatus.NoUserForToken
    // case 6: PasswordStatus.LoginFailed
  }
};

const CreatePasswordPage = (props: CreatePasswordPageProps) => {
  const { email, isValidToken, onCreatePassword } = props;

  useTitle(g11n.format('login_create_password'));

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onCreatePasswordClick = useCallback(
    async e => {
      e?.preventDefault();
      setIsLoading(true);

      const response = await onCreatePassword(newPassword, confirmNewPassword);

      if (response && response.success) {
        window.location.href = response.forward ?? '/login';
        return;
      }

      const error = getErrorMessage(response?.error);
      if (error) {
        setErrorMessage(error);
      }

      setIsLoading(false);
    },
    [confirmNewPassword, newPassword, onCreatePassword],
  );

  const onSetNewPassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewPassword(e.target.value);

      if (errorMessage !== null) {
        setErrorMessage(null);
      }
    },
    [errorMessage],
  );
  const onSetConfirmNewPassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfirmNewPassword(e.target.value);

      if (errorMessage !== null) {
        setErrorMessage(null);
      }
    },
    [errorMessage],
  );

  const isFormValid = useCallback(() => {
    const passwordsMatch = newPassword === confirmNewPassword;
    const passwordIsLongEnough = newPassword.length > 7;
    const containsNumbers = /[0-9]/.test(newPassword);
    const containsLetters = /[a-zA-Z]/.test(newPassword);

    return passwordsMatch && passwordIsLongEnough && containsNumbers && containsLetters;
  }, [confirmNewPassword, newPassword]);

  return (
    <PageBackground environment="dark" backButton={{ href: '/login' }}>
      <form className={styles.formContainer}>
        <Link href="/" type="wrapper" className={styles.hudlLogoContainer}>
          <IconLogoMark size="large" />
        </Link>

        <FormModifier className={styles.formModifierContainer} formSize="large" density="default">
          <Headline level="3" className={styles.letsResetYourPassword}>
            {g11n.format('login_reset_create_password')}
          </Headline>

          {!isValidToken && (
            <Fragment>
              <Text>{g11n.format('login_reset_expired_info')}</Text>
              <Text className="uni-margin--one--top">
                <FormattedHTMLMessage id="login_reset_again" />
              </Text>
            </Fragment>
          )}

          {isValidToken && (
            <Fragment>
              {email && (
                <Subhead className={styles.email} level="default">
                  {email}
                </Subhead>
              )}

              <Input
                id="password"
                type="password"
                label={g11n.format('login_reset_password_new_password')}
                qaId="password"
                className="uni-margin--one--top"
                helpText={g11n.format('login_reset_password_policy')}
                onChange={onSetNewPassword}
                value={newPassword}
                autoComplete="off"
              />

              <Input
                id="confirm-password"
                type="password"
                label={g11n.format('login_reset_password_confirm_new_password')}
                qaId="confirm-password"
                className="uni-margin--one--top uni-margin--one--bottom"
                onChange={onSetConfirmNewPassword}
                value={confirmNewPassword}
                autoComplete="off"
              />

              <ErrorDisplay>{errorMessage}</ErrorDisplay>

              <Button
                type="submit"
                isDisabled={isLoading || !isFormValid()}
                onClick={onCreatePasswordClick}
                className="uni-margin--one--top">
                {g11n.format('login_reset_create_your_password')}
              </Button>

              <Text className="uni-margin--half--top" level="small" color="subtle">
                <FormattedHTMLMessage id="login_reset_agreement_disclaimer" />
              </Text>
            </Fragment>
          )}
        </FormModifier>
      </form>
    </PageBackground>
  );
};

export default CreatePasswordPage;
